import { useNavigate } from "react-router";
import { Switch } from "@material-tailwind/react";
import { ReactComponent as EditIcon } from "../../../assets/icons/tables/edit-icon.svg";
import { ReactComponent as TrashIcon } from "../../../assets/icons/tables/trash-icon.svg";
import {
  IChallenge,
  usePartnerChallenges,
} from "../../../store/usePartnerChallenges";

const parcelDirectionLabel = (direction?: string) => {
  if (direction === "PICKUP") return "pickup only";
  if (direction === "DROPOFF") return "drop-off only";
  return "pickups and drop-offs";
};

/** Carrier ids arrive populated or raw; show names when we have them. */
const carrierNames = (challenge: any) =>
  (challenge?.conditions?.carrierPartnerIds ?? [])
    .map((c: any) => c?.name)
    .filter(Boolean)
    .join(", ");

interface ChallengesTableProps {
  challenges: IChallenge[];
  /** Rows of challenges that no longer run; drawn faded. */
  ended?: boolean;
}

export const ChallengesTable = ({
  challenges,
  ended = false,
}: ChallengesTableProps) => {
  /** Hooks */
  const { deletePartnerChallenge, updatePartnerChallenge } =
    usePartnerChallenges();
  const navigate = useNavigate();

  const isScheduled = (challenge: any) => {
    const startsAt = challenge?.startsAt;
    return startsAt != null && new Date(startsAt).getTime() > Date.now();
  };

  const hasEndedByDate = (challenge: any) => {
    const endsAt = challenge?.endsAt;
    return endsAt != null && new Date(endsAt).getTime() < Date.now();
  };

  const renderRow = (challenge: any) => (
    <tr
      key={challenge?._id}
      className={`bg-white border-b ${ended ? "opacity-60" : ""}`}
    >
              <td
                onClick={() =>
                  navigate(`/edit-partner-challenge/${challenge?._id}`)
                }
                className="px-2 sm:px-6 py-1 sm:py-4 whitespace-nowrap cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <span>{challenge?.en_title}</span>
                  {(challenge as any)?.needsVoucher ? (
                    <span
                      title={
                        ended
                          ? "No voucher for this partner: it would stay hidden in the app if this challenge is started again"
                          : "Hidden in the app until a voucher is assigned to this partner"
                      }
                      className="inline-flex items-center rounded-full bg-red-100 text-red-700 text-xs font-medium px-2 py-0.5 whitespace-nowrap"
                    >
                      Needs voucher
                    </span>
                  ) : null}
                  {/* Says why a row with its Active switch still on is
                      listed with the inactive challenges. */}
                  {hasEndedByDate(challenge) ? (
                    <span
                      title="Its end date has passed, so it no longer runs"
                      className="inline-flex items-center rounded-full bg-gray-200 text-gray-700 text-xs font-medium px-2 py-0.5 whitespace-nowrap"
                    >
                      Ended
                    </span>
                  ) : null}
                  {isScheduled(challenge) ? (
                    <span
                      title="Starts on its start date; until then it is not shown in the app"
                      className="inline-flex items-center rounded-full bg-amber-100 text-amber-700 text-xs font-medium px-2 py-0.5 whitespace-nowrap"
                    >
                      Scheduled
                    </span>
                  ) : null}
                </div>
              </td>
              <td
                onClick={() =>
                  navigate(`/edit-partner-challenge/${challenge?._id}`)
                }
                className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4 cursor-pointer"
              >
                {challenge?.en_description.length > 50
                  ? challenge?.en_description.substring(0, 50) + "..."
                  : challenge?.en_description}
              </td>
              <td
                onClick={() => navigate(`/edit-community/${challenge?._id}`)}
                className="px-2 sm:px-6 py-1 sm:py-4 cursor-pointer"
              >
                {challenge?.locationIds?.length
                  ? `${challenge.locationIds.length} store${
                      challenge.locationIds.length === 1 ? "" : "s"
                    }`
                  : challenge?.cities?.join(", ")}
              </td>
              <td
                onClick={() =>
                  navigate(`/edit-partner-challenge/${challenge?._id}`)
                }
                className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4 cursor-pointer"
              >
                {challenge?.conditions?.type}
                {challenge?.conditions?.type === "PARCEL_COUNT" ? (
                  <span className="block text-xs text-gray-600">
                    {challenge?.allowMultipleCompletions ? "repeatable · " : ""}
                    {challenge?.conditions?.minParcelCount ?? "?"} parcels
                    {" · "}
                    {parcelDirectionLabel(
                      challenge?.conditions?.parcelDirection
                    )}
                    {carrierNames(challenge) ? (
                      <>
                        {" · "}
                        {carrierNames(challenge)}
                      </>
                    ) : null}
                  </span>
                ) : challenge?.allowMultipleCompletions ? (
                  <span className="block text-xs text-gray-600">
                    repeatable
                  </span>
                ) : null}
              </td>
              <td
                onClick={() =>
                  navigate(`/edit-partner-challenge/${challenge?._id}`)
                }
                className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4 cursor-pointer"
              >
                {/* @ts-ignore */}
                {challenge?.partnerId?.name}
              </td>
              {/* Usable stock, counted the way the claim counts it: a voucher
                  that is expired, spent or belongs to a drop is not here. */}
              <td className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4">
                <span
                  className={
                    (challenge as any)?.challengeVoucherCount
                      ? "block"
                      : "block text-gray-600"
                  }
                >
                  {(challenge as any)?.challengeVoucherCount ?? 0} for this
                  challenge
                </span>
                <span className="block text-xs text-gray-600">
                  {(challenge as any)?.partnerVoucherCount ?? 0} for the partner
                </span>
              </td>
              {/* A repeating challenge is never finished, so one earned cycle
                  already counts as completed here. */}
              <td className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4">
                <span className="block">
                  {(challenge as any)?.completedCount ?? 0} completed
                </span>
                <span className="block text-xs text-gray-600">
                  {(challenge as any)?.inProgressCount ?? 0} in progress
                </span>
              </td>
              <td className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4">
                <Switch
                  crossOrigin={"anonymous"}
                  color="teal"
                  ripple
                  checked={(challenge as any)?.showParticipants || false}
                  onChange={(e) => {
                    updatePartnerChallenge(challenge?._id as string, {
                      showParticipants: e.target.checked,
                    } as any);
                  }}
                />
              </td>
              <td className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4">
                <Switch
                  crossOrigin={"anonymous"}
                  color="orange"
                  ripple
                  checked={(challenge as any)?.isTrending || false}
                  onChange={(e) => {
                    updatePartnerChallenge(challenge?._id as string, {
                      isTrending: e.target.checked,
                    } as any);
                  }}
                />
              </td>
              {/* The way to stop a challenge: one that people have played
                  cannot be deleted, so it is switched off instead. It then
                  stops counting progress and paying rewards. */}
              <td className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4">
                <Switch
                  crossOrigin={"anonymous"}
                  color="teal"
                  ripple
                  checked={
                    !(challenge as any)?.status ||
                    (challenge as any)?.status === "ACTIVE"
                  }
                  onChange={(e) => {
                    const active = e.target.checked;
                    if (
                      !active &&
                      // eslint-disable-next-line no-restricted-globals
                      !window.confirm(
                        "Stop this challenge?\n\nIt will stop counting progress and paying rewards. What customers already earned is kept."
                      )
                    ) {
                      return;
                    }
                    updatePartnerChallenge(challenge?._id as string, {
                      status: active ? "ACTIVE" : "INACTIVE",
                    } as any);
                  }}
                />
              </td>
              <td className="px-2 sm:px-6 py-1 sm:py-4 flex items-center justify-end cursor-pointer">
                <EditIcon
                  onClick={() =>
                    navigate(`/edit-partner-challenge/${challenge?._id}`)
                  }
                  className="mr-3 sm:mr-7"
                />
                {/* Played challenges cannot be deleted (the server refuses);
                    they are stopped with the Active switch instead. */}
                {(challenge?.participantCount ?? 0) > 0 ? null : (
                  <TrashIcon
                    onClick={() => {
                      const confirmDelete = window.confirm(
                        "Are you sure you want to delete this Partner Challenge?"
                      );
                      if (confirmDelete) {
                        deletePartnerChallenge(challenge?._id as string);
                      }
                    }}
                  />
                )}
              </td>
    </tr>
  );

  return (
    <div className="relative overflow-x-auto">
      <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
        <thead className="font-medium bg-gray-50">
          <tr>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4">
              Title
            </th>
            <th
              scope="col"
              className="hidden min-w-[250px] sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Description
            </th>
            <th scope="col" className="px-2 min-w-[240px] sm:px-6 py-1 sm:py-4">
              Cities
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Type
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Partner
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Vouchers
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Players
            </th>
            <th
              scope="col"
              className="hidden min-w-[240px] sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Show participants in app
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Trending
            </th>
            <th
              scope="col"
              className="hidden sm:table-cell px-2 sm:px-6 py-1 sm:py-4"
            >
              Active
            </th>
            <th scope="col" className="px-2 sm:px-6 py-1 sm:py-4 text-right">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {challenges.map((challenge) => renderRow(challenge))}
        </tbody>
      </table>
    </div>
  );
};
